import React, { useMemo } from "react"

import { OneVerifyEraUploadFileFormat } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import refreshIcon from "@front-shared/assets/images/icons/refresh-dark2.svg"

export const EraUploadReviewFloatingControlComponent: React.FunctionComponent<
    IEraUploadReviewFloatingControlComponentProps
> = props => {
    const isX12Upload =
        props.fileFormat === OneVerifyEraUploadFileFormat.x12

    const statusLabel = useMemo(() => {
        if (isX12Upload) {
            return "X12 835 file ready"
        }

        return (
            <>
                <span className="text-bold">
                    {props.previewRowCount.toLocaleString()} of{" "}
                    {props.totalRowCount.toLocaleString()} row
                    {props.totalRowCount === 1 ? "" : "s"}
                </span>{" "}
                {props.isPreviewTruncated ? "in preview" : "ready"}
            </>
        )
    }, [
        isX12Upload,
        props.previewRowCount,
        props.totalRowCount,
        props.isPreviewTruncated,
    ])

    return (
        <div
            className="
                fixed bottom-[24px] horizontal-center flex items-center z-20
                bg-brand-white rounded-[100px]
                pl-[32px] py-[12px] pr-[12px]
                gap-[16px] min-w-[320px] max-w-[90vw]
                border border-solid border-brand-vibrantPurple
            "
        >
            <div className="flex items-center gap-[8px] flex-shrink-0 min-w-0">
                <span
                    className="
                        flex-shrink-0 w-[20px] h-[20px] rounded-full flex items-center justify-center
                        bg-brand-green3 text-white text-[12px] font-bold
                    "
                    aria-hidden
                >
                    ✓
                </span>

                <PrimaryText
                    typography="text"
                    size={14}
                    className="whitespace-nowrap"
                >
                    {statusLabel}
                </PrimaryText>
            </div>

            <div className="flex items-center gap-[16px] flex-shrink-0">
                <ButtonElement
                    label={isX12Upload ? "Re-upload file" : "Re-upload CSV"}
                    htmlType="button"
                    type="default"
                    onClick={props.onReupload}
                    icon={refreshIcon}
                    buttonClassName="!pl-[16px] !pr-[16px]"
                />

                <ButtonElement
                    label="Upload to secure storage"
                    htmlType="button"
                    type="primary"
                    onClick={props.onUpload}
                    isLoading={props.isUploading}
                    buttonClassName="!pl-[16px] !pr-[16px]"
                    disabled={props.isUploading}
                />
            </div>
        </div>
    )
}

interface IEraUploadReviewFloatingControlComponentProps {
    fileFormat: OneVerifyEraUploadFileFormat
    previewRowCount: number
    totalRowCount: number
    isPreviewTruncated: boolean
    onReupload: () => void
    onUpload: () => void
    isUploading?: boolean
}
